/**
 * /app/dashboards — Dashboard index page.
 *
 * Lists dashboards (My + Pack-shipped + Shared). Opening one renders the full
 * drag-and-drop editor (dashboard-view.tsx); in live mode dashboards are
 * server-persisted per-org via /v1/iot/dashboards.
 *
 * The editor (drag-and-drop palette + react-grid-layout + Entity Alias
 * data-source picker) is specced in docs/06_iot_twin_alignment.md § 5.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  Activity,
  Clock,
  Grid3x3,
  LineChart,
  Lock,
  Plus,
  Search,
  Workflow,
} from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Card, CardContent } from "@/lib/ui/card";
import {
  TooltipProvider,
} from "@/lib/ui/tooltip";
import { cn } from "@/lib/ui/cn";
import { type DashboardSource } from "@/lib/widgets/__fixtures";
import { useDashboards } from "@/lib/dashboard/store";
import { useOrg } from "@/lib/org-context";
import type { DashboardConfig } from "@/lib/dashboard/types";
import { CreateDashboardDialog } from "@/components/dashboards/CreateDashboardDialog";

type SourceFilter = "all" | DashboardSource;

const FILTER_DEFS: Array<{ key: SourceFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "my", label: "My dashboards" },
  { key: "pack", label: "Pack-shipped" },
  { key: "shared", label: "Shared with me" },
];

const SOURCE_BADGE: Record<DashboardSource, { label: string; variant: "default" | "secondary" | "info" | "warning" | "success" }> = {
  my: { label: "My", variant: "default" },
  pack: { label: "Pack", variant: "info" },
  shared: { label: "Shared", variant: "secondary" },
};

/**
 * Pick a thumbnail icon based on the widgets a dashboard contains.
 * Fallback to "mixed" — the icon is decorative.
 */
function thumbnailIcon(d: DashboardConfig): typeof LineChart {
  const types = new Set(d.widgets.map((w) => w.type));
  if (types.has("process-graph" as never)) return Workflow;
  if (types.has("timeseries") || types.has("spc") || types.has("histogram")) {
    return LineChart;
  }
  if (types.has("table")) return Grid3x3;
  if (types.has("kpi") || types.has("gauge")) return Grid3x3;
  return Activity;
}

export function Dashboards() {
  const { dashboards } = useDashboards();
  const { can } = useOrg();
  // Dashboard authoring (new/edit/save/delete/add-widget) → dashboards.write.
  const canWrite = can("dashboards.write");
  const [filter, setFilter] = useState<SourceFilter>("all");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = useMemo(() => {
    let rows = dashboards;
    if (filter !== "all") rows = rows.filter((r) => r.source === filter);
    if (search) {
      const needle = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.name.toLowerCase().includes(needle) ||
          (r.description ?? "").toLowerCase().includes(needle),
      );
    }
    return rows;
  }, [dashboards, filter, search]);

  const counts = useMemo(
    () => ({
      all: dashboards.length,
      my: dashboards.filter((d) => d.source === "my").length,
      pack: dashboards.filter((d) => d.source === "pack").length,
      shared: dashboards.filter((d) => d.source === "shared").length,
    }),
    [dashboards],
  );

  return (
    <TooltipProvider delayDuration={250}>
      <PageContainer className="max-w-7xl">
        <PageHeader
          breadcrumb={
            <>
              <Link to="/app" className="hover:text-on-surface">
                App
              </Link>
              <span aria-hidden>/</span>
              <span className="text-on-surface">Dashboards</span>
            </>
          }
          title="Dashboards"
          description="Compose KPI grids, time-series charts, alarm feeds and process views, from tags or from your equipment."
          actions={
            canWrite ? (
              <Button size="sm" data-tour="dashboard-new-btn" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-1.5 size-4" />
                New dashboard
              </Button>
            ) : undefined
          }
        />

        <CreateDashboardDialog open={createOpen} onClose={() => setCreateOpen(false)} />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {FILTER_DEFS.map((def) => (
              <button
                key={def.key}
                type="button"
                onClick={() => setFilter(def.key)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  filter === def.key
                    ? "bg-brand text-brand-foreground"
                    : "bg-surface-muted text-on-surface-muted hover:bg-surface-muted/70 hover:text-on-surface",
                )}
              >
                {def.label}
                <span
                  className={cn(
                    "ml-1.5 rounded-full px-1.5 py-0.5 font-mono text-[10px]",
                    filter === def.key
                      ? "bg-brand-foreground/20"
                      : "bg-surface/60",
                  )}
                >
                  {counts[def.key]}
                </span>
              </button>
            ))}
          </div>
          <div className="relative ml-auto w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-on-surface-muted" />
            <Input
              placeholder="Search dashboards…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        {/* Grid of dashboard cards */}
        {filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-elevated/40 p-12 text-center">
            <p className="text-sm text-on-surface-muted">
              {dashboards.length === 0
                ? "No dashboards yet. A dashboard puts your equipment's live values, charts and alarms on one screen."
                : "No dashboards match the current filter."}
            </p>
            {dashboards.length === 0 && canWrite && (
              <Button size="sm" className="mt-3" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-1.5 size-4" />
                New dashboard
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-tour="dashboards-grid">
            {filtered.map((d) => (
              <DashboardCard key={d.id} dashboard={d} />
            ))}
          </div>
        )}

      </PageContainer>
    </TooltipProvider>
  );
}

function DashboardCard({ dashboard }: { dashboard: DashboardConfig }) {
  const badge = SOURCE_BADGE[dashboard.source];
  const ThumbIcon = thumbnailIcon(dashboard);

  return (
    <Card className="group flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md">
      {/* Thumbnail (placeholder gradient + icon) */}
      <Link
        to={`/app/dashboards/${dashboard.id}`}
        className="relative flex aspect-[16/9] items-center justify-center bg-gradient-to-br from-brand-muted via-surface-elevated to-surface text-brand"
      >
        <ThumbIcon className="size-12 opacity-60" strokeWidth={1.2} />
        {dashboard.source === "pack" && (
          <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-surface-elevated/80 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-on-surface-muted backdrop-blur">
            <Lock className="size-2.5" />
            Read-only
          </div>
        )}
      </Link>

      <CardContent className="flex flex-1 flex-col gap-3 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <Link
              to={`/app/dashboards/${dashboard.id}`}
              className="text-sm font-semibold text-on-surface hover:text-brand"
            >
              {dashboard.name}
            </Link>
            <div className="flex items-center gap-1.5 text-[11px] text-on-surface-muted">
              <Badge variant={badge.variant}>{badge.label}</Badge>
              {dashboard.pack && (
                <span className="font-mono">{dashboard.pack}</span>
              )}
            </div>
          </div>
        </div>

        <p className="line-clamp-2 text-xs text-on-surface-muted">
          {dashboard.description ?? ""}
        </p>

        <div className="mt-auto flex items-center justify-between border-t border-border pt-3 text-[11px] text-on-surface-muted">
          <span className="flex items-center gap-1">
            <Grid3x3 className="size-3" />
            {dashboard.widgets.length} widgets
          </span>
          <span className="flex items-center gap-1">
            <Clock className="size-3" />
            {formatRelative(dashboard.updatedAt)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function formatRelative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}
